import * as React from "react";
import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import DashboardShellLayout from "@/app/(dashboard)/layout";
import SettingsSectionLayout from "@/app/(dashboard)/settings/layout";
import Link from "@/components/link";
import { useMessages } from "@/locales";

const OrderServicesPage = React.lazy(() => import("@/app/(dashboard)/order-services/page"));
const BillingUploadPage = React.lazy(() => import("@/app/(dashboard)/billing-upload/page"));
const OrderServiceDetailPage = React.lazy(() => import("@/app/(dashboard)/order-services/[id]/page"));
const DashboardPage = React.lazy(() => import("@/app/(dashboard)/page"));
const ApprovalsPage = React.lazy(() => import("@/app/(dashboard)/approvals/page"));
const TicketsPage = React.lazy(() => import("@/app/(dashboard)/tickets/page"));
const TicketDetailPage = React.lazy(() => import("@/app/(dashboard)/tickets/[id]/page"));
const ExceptionsPage = React.lazy(() => import("@/app/(dashboard)/exceptions/page"));
const TasksPage = React.lazy(() => import("@/app/(dashboard)/tasks/page"));
const AnnouncementsPage = React.lazy(() => import("@/app/(dashboard)/announcements/page"));
const NewAnnouncementPage = React.lazy(() => import("@/app/(dashboard)/announcements/new/page"));
const AdminPage = React.lazy(() => import("@/app/(dashboard)/admin/page"));
const UsersPage = React.lazy(() => import("@/app/(dashboard)/users/page"));
const ConnectionsPage = React.lazy(() => import("@/app/(dashboard)/connections/page"));
const SettingsPage = React.lazy(() => import("@/app/(dashboard)/settings/page"));
const NewOrderServicePage = React.lazy(() => import("@/app/(dashboard)/order-services/new/page"));
const SettingsOrganizationPage = React.lazy(() => import("@/app/(dashboard)/settings/organization/page"));
const SettingsPasswordPage = React.lazy(() => import("@/app/(dashboard)/settings/password/page"));
const SettingsSecurityPage = React.lazy(() => import("@/app/(dashboard)/settings/security/page"));
const SettingsSessionsPage = React.lazy(() => import("@/app/(dashboard)/settings/sessions/page"));
const SettingsNotificationsPage = React.lazy(() => import("@/app/(dashboard)/settings/notifications/page"));
const SettingsBranchesPage = React.lazy(() => import("@/app/(dashboard)/settings/branches/page"));
const SettingsInvitationsPage = React.lazy(() => import("@/app/(dashboard)/settings/invitations/page"));
const SettingsApiKeysPage = React.lazy(() => import("@/app/(dashboard)/settings/api-keys/page"));
const SettingsAuditPage = React.lazy(() => import("@/app/(dashboard)/settings/audit/page"));
const SettingsWebhooksPage = React.lazy(() => import("@/app/(dashboard)/settings/webhooks/page"));
const SettingsPlatformPage = React.lazy(() => import("@/app/(dashboard)/settings/platform/page"));
const SettingsVerificationPage = React.lazy(() => import("@/app/(dashboard)/settings/verification/page"));
const OrganizationsPage = React.lazy(() => import("@/app/(dashboard)/admin/organizations/page"));
const NewOrganizationPage = React.lazy(() => import("@/app/(dashboard)/admin/organizations/new/page"));
const OrganizationDetailPage = React.lazy(() => import("@/app/(dashboard)/admin/organizations/[id]/page"));
const EditOrganizationPage = React.lazy(() => import("@/app/(dashboard)/admin/organizations/[id]/edit/page"));
const NewEndCustomerPage = React.lazy(() => import("@/app/(dashboard)/admin/organizations/[id]/end-customers/new/page"));
const EditEndCustomerPage = React.lazy(() => import("@/app/(dashboard)/admin/organizations/[id]/end-customers/[endCustomerId]/edit/page"));
const RolesPage = React.lazy(() => import("@/app/(dashboard)/admin/roles/page"));
const NewRolePage = React.lazy(() => import("@/app/(dashboard)/admin/roles/new/page"));
const EditRolePage = React.lazy(() => import("@/app/(dashboard)/admin/roles/[id]/edit/page"));
const TeamsPage = React.lazy(() => import("@/app/(dashboard)/admin/teams/page"));
const WorkflowsPage = React.lazy(() => import("@/app/(dashboard)/admin/workflows/page"));
const ServiceTypesPage = React.lazy(() => import("@/app/(dashboard)/admin/service-types/page"));
const DocumentTypesPage = React.lazy(() => import("@/app/(dashboard)/admin/document-types/page"));
const CommentPresetsPage = React.lazy(() => import("@/app/(dashboard)/admin/comment-presets/page"));
const MasterDocumentsPage = React.lazy(() => import("@/app/(dashboard)/admin/master-documents/page"));
const StoresPage = React.lazy(() => import("@/app/(dashboard)/admin/stores/page"));
const NewStorePage = React.lazy(() => import("@/app/(dashboard)/admin/stores/new/page"));
const EditStorePage = React.lazy(() => import("@/app/(dashboard)/admin/stores/[id]/edit/page"));
const BillingPage = React.lazy(() => import("@/app/(dashboard)/admin/billing/page"));
const LoginPage = React.lazy(() => import("@/app/(auth)/login/page"));
const ForgotPasswordPage = React.lazy(() => import("@/app/(auth)/forgot-password/page"));
const ResetPasswordPage = React.lazy(() => import("@/app/(auth)/reset-password/page"));
const VerifyEmailPage = React.lazy(() => import("@/app/(auth)/verify-email/page"));
const AcceptInvitePage = React.lazy(() => import("@/app/(auth)/accept-invite/page"));
const TwoFactorSetupPage = React.lazy(() => import("@/app/(auth)/2fa-setup/page"));

function authGuard() {
  if (localStorage.getItem("lwp.session") !== "1") throw redirect({ to: "/login" });
}

function RouteLoading() {
  const m = useMessages();
  return <div className="p-6 text-sm text-muted-foreground">{m.common.loading}</div>;
}

function LazyPage({ page: Page }: { page: React.LazyExoticComponent<React.ComponentType> }) {
  return (
    <React.Suspense fallback={<RouteLoading />}>
      <Page />
    </React.Suspense>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <DashboardShellLayout>{children}</DashboardShellLayout>;
}

function SettingsShell({ children }: { children: React.ReactNode }) {
  return (
    <Shell>
      <SettingsSectionLayout>{children}</SettingsSectionLayout>
    </Shell>
  );
}

function NotFoundPage() {
  const m = useMessages();
  return (
    <div className="mx-auto mt-24 max-w-md p-6 text-center">
      <h1 className="text-2xl font-semibold">{m.common.pageNotFound}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{m.common.pageNotFoundHint}</p>
      <Link className="mt-4 inline-block text-sm underline" href="/">
        {m.common.goHome}
      </Link>
    </div>
  );
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
});

function publicRoute(path: string, page: React.LazyExoticComponent<React.ComponentType>) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    component: () => <LazyPage page={page} />,
  });
}

function protectedRoute(path: string, page: React.LazyExoticComponent<React.ComponentType>) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: authGuard,
    component: () => (
      <Shell>
        <LazyPage page={page} />
      </Shell>
    ),
  });
}

function settingsRoute(path: string, page: React.LazyExoticComponent<React.ComponentType>) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: authGuard,
    component: () => (
      <SettingsShell>
        <LazyPage page={page} />
      </SettingsShell>
    ),
  });
}

const routeTree = rootRoute.addChildren([
  publicRoute("/login", LoginPage),
  publicRoute("/forgot-password", ForgotPasswordPage),
  publicRoute("/reset-password", ResetPasswordPage),
  publicRoute("/verify-email", VerifyEmailPage),
  publicRoute("/accept-invite", AcceptInvitePage),
  publicRoute("/2fa-setup", TwoFactorSetupPage),
  // Invitation, reset, and verify emails use an /auth prefix. Keep the short paths for in-app links.
  publicRoute("/auth/accept-invite", AcceptInvitePage),
  publicRoute("/auth/reset-password", ResetPasswordPage),
  publicRoute("/auth/verify-email", VerifyEmailPage),
  protectedRoute("/", DashboardPage),
  protectedRoute("/order-services", OrderServicesPage),
  protectedRoute("/billing-upload", BillingUploadPage),
  protectedRoute("/order-services/new", NewOrderServicePage),
  protectedRoute("/order-services/$id", OrderServiceDetailPage),
  protectedRoute("/approvals", ApprovalsPage),
  protectedRoute("/tickets", TicketsPage),
  protectedRoute("/tickets/$id", TicketDetailPage),
  protectedRoute("/exceptions", ExceptionsPage),
  protectedRoute("/tasks", TasksPage),
  protectedRoute("/announcements", AnnouncementsPage),
  protectedRoute("/announcements/new", NewAnnouncementPage),
  protectedRoute("/admin", AdminPage),
  protectedRoute("/users", UsersPage),
  protectedRoute("/connections", ConnectionsPage),
  protectedRoute("/admin/organizations", OrganizationsPage),
  protectedRoute("/admin/organizations/new", NewOrganizationPage),
  protectedRoute("/admin/organizations/$id", OrganizationDetailPage),
  protectedRoute("/admin/organizations/$id/edit", EditOrganizationPage),
  protectedRoute("/admin/organizations/$id/end-customers/new", NewEndCustomerPage),
  protectedRoute("/admin/organizations/$id/end-customers/$endCustomerId/edit", EditEndCustomerPage),
  protectedRoute("/admin/roles", RolesPage),
  protectedRoute("/admin/roles/new", NewRolePage),
  protectedRoute("/admin/roles/$id/edit", EditRolePage),
  protectedRoute("/admin/teams", TeamsPage),
  protectedRoute("/admin/workflows", WorkflowsPage),
  protectedRoute("/admin/service-types", ServiceTypesPage),
  protectedRoute("/admin/document-types", DocumentTypesPage),
  protectedRoute("/admin/comment-presets", CommentPresetsPage),
  protectedRoute("/admin/master-documents", MasterDocumentsPage),
  protectedRoute("/admin/stores", StoresPage),
  protectedRoute("/admin/stores/new", NewStorePage),
  protectedRoute("/admin/stores/$id/edit", EditStorePage),
  protectedRoute("/admin/billing", BillingPage),
  settingsRoute("/settings", SettingsPage),
  settingsRoute("/settings/organization", SettingsOrganizationPage),
  settingsRoute("/settings/password", SettingsPasswordPage),
  settingsRoute("/settings/security", SettingsSecurityPage),
  settingsRoute("/settings/sessions", SettingsSessionsPage),
  settingsRoute("/settings/notifications", SettingsNotificationsPage),
  settingsRoute("/settings/branches", SettingsBranchesPage),
  settingsRoute("/settings/invitations", SettingsInvitationsPage),
  settingsRoute("/settings/api-keys", SettingsApiKeysPage),
  settingsRoute("/settings/audit", SettingsAuditPage),
  settingsRoute("/settings/webhooks", SettingsWebhooksPage),
  settingsRoute("/settings/platform", SettingsPlatformPage),
  settingsRoute("/settings/verification", SettingsVerificationPage),
]);

const router = createRouter({ routeTree });

export function App() {
  return <RouterProvider router={router} />;
}
